import { createRef } from 'react';
import { createPortal } from 'react-dom';

import { JxMinimizableFormBase, jxWindowManager } from '@jx';
import { umlCanvasEngine, umlConnectorEngine, umlViewportEngine, umlSelectionEngine } from '@jxUml';
import { handleAddPackage, handleAddClass, handleAddRemarks, handleAddIconShape } from './UmlCanvasToolbarSpawnHelper.js';

import UmlCanvasTitlebar from './UmlCanvasTitlebar.jsx';
import UmlCanvasToolbarButtonWidget from './UmlCanvasToolbarButtonWidget.jsx';
import UmlCanvasToolbarInfoRows from './UmlCanvasToolbarInfoRows.jsx';
import { resolveSelectedInfo } from './UmlCanvasToolbarHelpers.js';
import { runAutoLayout } from '../../Core/AutoLayout/AutoLayoutRunner.js';
import logService from '../../../services/wss/logService3.js';
import UserSettingsContext from '../../../contexts/UserSettingsContext.jsx';
import { saveCurrentSchema } from '../../Core/UmlCanvasSaveAction.js';
import cardPainterManager from '../../../Widgets/DataPainterWidget/Core/CardPainterManager.jsx';
import QuestionDlg from '../../../components/shared/QuestionDlg/QuestionDlg.jsx';
import UmlPreviewDlg from './UmlPreviewDlg.jsx';
import { setPreviewUpdateListener } from '../../Core/Preview/UmlPreviewTrigger.js';
import UmlExportImageDlg from './ExportImage/UmlExportImageDlg.jsx';
import UmlPkgSaveDlg from '../../UmlPkgLib/UmlPkgSaveDlg/UmlPkgSaveDlg.jsx';
import componentNotifier from '../../../services/wss/ComponentNotifier';
import { EngineNames } from '../../../services/wss/events/EngineNames';
import UmlPkgLibViewerDlg from '../../UmlPkgLib/UmlPkgLibViewerDlg/UmlPkgLibViewerDlg.jsx';
import './UmlCanvasToolbar.css';

// const DEFAULT_POS = { top: 10, left: 10 };
const WORKSPACE_CARD_ID = 'uml:workspace:active'; // matches openCanvasCard's `${meta.type}:${meta.target}` id (UmlSchemaConfigDlg.jsx)

/**
 * Always-visible canvas toolbar, portaled to document.body. Button row lives in
 * UmlCanvasToolbarButtonWidget.jsx / UmlCanvasToolbarButtons.jsx, info rows in
 * UmlCanvasToolbarInfoRows.jsx, spawn logic in UmlCanvasToolbarSpawnHelper.js.
 * @param {object} canvasRef - .uml-canvas DOM ref from UmlCanvas.jsx, needed for nextPosition
 */
export default class UmlCanvasToolbar extends JxMinimizableFormBase {
    static contextType = UserSettingsContext;

    state = {
        ...this.state,
        hideSelectedRow: false,
        hideHoverRow: true,
        previewDlgOpen: false,
        exportDlgOpen: false,
        closeConfirmOpen: false,
        pkgLibViewerOpen: false,
    };

    constructor(props) {
        super(props);
        this.name = 'UmlCanvasToolbar';
        this.toolbarRef = createRef();
    }

    componentDidMount() {
        super.componentDidMount();

        // Constant windowId fallback (no id prop from parent) means jxWindowCache's Minimized
        // flag can persist across mounts — reset it here so a fresh mount never stays hidden.
        if (this.isMinimized) {
            jxWindowManager.restoreWindow(this.getId());
        }

        this._unsubscribeCanvas = umlCanvasEngine.subscribe(() => this.forceUpdate());
        this._unsubscribeViewport = umlViewportEngine.subscribe(() => this.forceUpdate());
        this._unsubscribeSelection = umlSelectionEngine.subscribe(() => this.forceUpdate());
        setPreviewUpdateListener(this.handleUpdatePreview);
    }

    componentWillUnmount() {
        this._unsubscribeCanvas?.();
        this._unsubscribeViewport?.();
        this._unsubscribeSelection?.();
        setPreviewUpdateListener(null);
        super.componentWillUnmount();
    }

    // Literal, hardcoded default — always-visible toolbar, fixed corner spot.
    // width/height not read by panelStyle (JxFormDraggableBase, non-resizable) — CSS owns
    // the real size, this is only for geo-shape consistency.
    getDefaultGeo() {
        return { top: 10, left: 10, width: 480, height: 300 };
    }

    getTaskbarTitle() {
        return umlCanvasEngine.loadedSchemaInfo?.name || 'Uml Toolbar';
    }

    handleAddPackage = () => handleAddPackage(this);
    handleAddClass = () => handleAddClass(this);
    handleAddRemarks = () => handleAddRemarks(this);
    handleAddIconShape = () => handleAddIconShape(this);

    handleToggleSnap = () => {
        const next = !this.context.settings['umlWorkspace.snapToGrid'];
        this.context.setSetting('umlWorkspace.snapToGrid', next);
    };

    handleGridSizeChange = (value) => {
        const next = Number(value);
        if (!next || next <= 0) return; // ignore empty/invalid while typing
        this.context.setSetting('umlWorkspace.gridSize', next);
    };

    handleToggleHideInterfaces = () => {
        umlCanvasEngine.setHideInterfaces(!umlCanvasEngine.viewConfig.hideInterfaces);
    };

    handleFixLines = () => {
        umlConnectorEngine.straightenAllConnectors(
            'straightenExisting',
            (shapeId) => umlCanvasEngine.getShape(shapeId),
            this.props.visibleConnectors,
        );
    };

    handleSaveSchema = async () => {
        await saveCurrentSchema();
    };

    // Update Preview button, and the S-key save shortcut (via UmlPreviewTrigger,
    // see UmlCanvas.useConnectorKeyboardShortcuts.js) both land here. Capture
    // logic itself lives in UmlPreviewDlg.
    handleUpdatePreview = () => {
        this.setState({ previewDlgOpen: true });
    };

    handleClosePreviewDlg = () => {
        this.setState({ previewDlgOpen: false });
    };

    handleOpenExportDlg = () => {
        this.setState({ exportDlgOpen: true });
    };

    handleCloseExportDlg = () => {
        this.setState({ exportDlgOpen: false });
    };

    // Opens the "Copy Package" save dialog via ComponentNotifier — UmlPkgSaveDlg
    // is self-contained and subscribes to this channel itself (see its
    // handleNotify). Both the C-key shortcut
    // (UmlCanvas.useConnectorKeyboardShortcuts.js) and this toolbar button go
    // through the same notify call, no local open/packageShape state needed here.
    handleCopyPackageButton = () => {
        const shape = umlCanvasEngine.getShape(umlSelectionEngine.selectedId);
        if (shape?.kind !== 'package') return;
        componentNotifier.notify(EngineNames.UmlPkgSaveDlg, 'Open', shape);
    };

    handleTogglePkgLibViewer = () => {
        this.setState((prev) => ({ pkgLibViewerOpen: !prev.pkgLibViewerOpen }));
    };

    handleClosePkgLibViewer = () => {
        this.setState({ pkgLibViewerOpen: false });
    };

    /**
     * Closes the workspace card if the canvas is clean; if there are unsaved
     * changes (umlCanvasEngine.isDirty()), asks for confirmation first.
     */
    handleCloseWorkspace = () => {
        if (umlCanvasEngine.isDirty()) {
            this.setState({ closeConfirmOpen: true });
            return;
        }
        this.closeWorkspace();
    };

    handleCancelCloseWorkspace = () => {
        this.setState({ closeConfirmOpen: false });
    };

    handleConfirmCloseWorkspace = () => {
        this.setState({ closeConfirmOpen: false });
        this.closeWorkspace();
    };

    /**
     * Resets canvas state and removes this card from Your Workspace via
     * cardPainterManager — frees the slot for a different widget type.
     */
    closeWorkspace = () => {
        umlCanvasEngine.loadFromSchema({ shapes: [], connectors: [] });
        umlCanvasEngine.setLoadedSchemaInfo({ rk: null, name: '', remarks: '' });
        cardPainterManager.closeCard(WORKSPACE_CARD_ID);
    };

    // Auto Layout v1 (rank-assignment only, see Core/AutoLayout/AutoLayoutRunner.js).
    handleAutoLayout = () => {
        const { positions, warnings } = runAutoLayout(umlCanvasEngine.shapes, umlConnectorEngine.connectors);
        positions.forEach(({ shapeId, x, y }) => umlCanvasEngine.updateShapePosition(shapeId, x, y));
        warnings.forEach((warning) => logService.logError(`AutoLayout: ${warning}`));
    };

    handleToggleHideSelectedRow = () => {
        this.setState((prev) => ({ hideSelectedRow: !prev.hideSelectedRow }));
    };

    handleToggleHideHoverRow = () => {
        this.setState((prev) => ({ hideHoverRow: !prev.hideHoverRow }));
    };

    render() {
        if (this.isMinimized) return null;

        const {
            hoveredShapeId,
            hoveredConnectorId,
            hoveredGeo,
            connectors,
            worldMapOpen,
            onToggleWorldMap,
            hiddenItemsOpen,
            onToggleHiddenItems,
        } = this.props;
        const selectedId = umlSelectionEngine.selectedId;
        const getShapeById = (shapeId) => umlCanvasEngine.getShape(shapeId);

        const selectedInfo = resolveSelectedInfo(selectedId, getShapeById, connectors);
        const hoveredShape = hoveredShapeId ? getShapeById(hoveredShapeId) : null;
        const hoveredConnector = hoveredConnectorId ? connectors.find((c) => c.id === hoveredConnectorId) : null;
        const isPackageSelected = getShapeById(selectedId)?.kind === 'package';

        return createPortal(
            <div
                {...this.panelProps}
                ref={this.toolbarRef}
                style={this.panelStyle}
                className={`jx-panel uct-toolbar ${this.panelActiveClass}`}
            >
                <UmlCanvasTitlebar
                    id="uct_titlebar"
                    title={umlCanvasEngine.loadedSchemaInfo?.name || 'Untitled'}
                    dragHandleProps={this.dragHandleProps}
                    onSave={this.handleSaveSchema}
                    hideSelectedRow={this.state.hideSelectedRow}
                    onToggleHideSelectedRow={this.handleToggleHideSelectedRow}
                    hideHoverRow={this.state.hideHoverRow}
                    onToggleHideHoverRow={this.handleToggleHideHoverRow}
                    isPinned={this.state.isPinned}
                    onTogglePin={this.togglePin}
                    onCloseWorkspace={this.handleCloseWorkspace}
                    isMinimizable={true}
                    windowId={this.getId()}
                />

                <UmlCanvasToolbarButtonWidget
                    toolbar={this}
                    worldMapOpen={worldMapOpen}
                    onToggleWorldMap={onToggleWorldMap}
                    hiddenItemsOpen={hiddenItemsOpen}
                    onToggleHiddenItems={onToggleHiddenItems}
                    isPackageSelected={isPackageSelected}
                    onCopyPackage={this.handleCopyPackageButton}
                    pkgLibViewerOpen={this.state.pkgLibViewerOpen}
                    onTogglePkgLibViewer={this.handleTogglePkgLibViewer}
                />

                <UmlCanvasToolbarInfoRows
                    hideSelectedRow={this.state.hideSelectedRow}
                    selectedInfo={selectedInfo}
                    hideHoverRow={this.state.hideHoverRow}
                    hoveredGeo={hoveredGeo}
                    hoveredConnector={hoveredConnector}
                    hoveredShape={hoveredShape}
                    getShapeById={getShapeById}
                    onMouseDown={this._onMouseDown}
                />

                <UmlPreviewDlg
                    open={this.state.previewDlgOpen}
                    note={umlCanvasEngine.loadedSchemaInfo}
                    onClose={this.handleClosePreviewDlg}
                />

                <UmlExportImageDlg
                    open={this.state.exportDlgOpen}
                    note={umlCanvasEngine.loadedSchemaInfo}
                    onClose={this.handleCloseExportDlg}
                />

                <UmlPkgSaveDlg />

                <UmlPkgLibViewerDlg open={this.state.pkgLibViewerOpen} onClose={this.handleClosePkgLibViewer} />

                <QuestionDlg
                    open={this.state.closeConfirmOpen}
                    title="Close Uml Workspace"
                    message="You have unsaved changes. Close anyway?"
                    buttons={[
                        { label: 'Cancel', onClick: this.handleCancelCloseWorkspace },
                        { label: 'Close', onClick: this.handleConfirmCloseWorkspace, color: '#ff6b6b' },
                    ]}
                />
            </div>,
            document.body,
        );
    }
}
